import React from 'react';

import { ArrowBack, Menu as MenuIcon } from '@mui/icons-material';
import { AppBar, IconButton, Toolbar, useTheme } from '@mui/material';
import InAppSpy from 'inapp-spy';
import { includes, some, trimEnd } from 'lodash';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { createScopedI18n } from '../../i18n/i18n';
import { useGoBack } from '../use_go_back';
import { AppDrawer } from './app_drawer';
import OpenBrowserButton from './open_browser_button';
import RestaurantJobHiringSelector from './restaurant_job_hiring_selector';
import { RestaurantSelector } from './restaurant_selector';
import { UserInfoMenuButton } from './user_info_menu_button';

const resetPasswordPageI18n = createScopedI18n('pages.reset_password');
const registerPageI18n = createScopedI18n('pages.register');
const firstProfileEditPageI18n = createScopedI18n('pages.first_profile_edit');
const navBarI18n = createScopedI18n('components.nav_bar');

const determineAuthPageTitle = (pathname: string) => {
  if (includes(['/auth/reset_password'], trimEnd(pathname, '/'))) {
    return resetPasswordPageI18n('title');
  }
  if (includes(['/auth/register'], trimEnd(pathname, '/'))) {
    return registerPageI18n('title');
  }
  if (includes(['/new_password'], trimEnd(pathname, '/'))) {
    return resetPasswordPageI18n('title_new');
  }
  return null;
};

export type NavBarProps = {
  hideNavBar?: boolean;
};

const NavBar = ({ hideNavBar }: NavBarProps) => {
  const theme = useTheme();

  const navigate = useNavigate();
  const goBack = useGoBack();

  const location = useLocation();
  const showDrawerMenu = includes(['/jobs', '/job_hirings', '/attendances'], trimEnd(location.pathname, '/'));
  const showGoBackMenu = !includes(
    ['/flow_sign_out', '/first_new_restaurant', '/first_profile_edit', '/force_change_password', '/pdpa_agreement'],
    trimEnd(location.pathname, '/'),
  );

  // Paths to show restaurant selector: /jobs, /jobs/new, /jobs/period_jobs/new, /restaurants/:id/*, /attendances
  const RESTAURANT_SELECTOR_PATHS = [
    '/jobs',
    '/jobs/new',
    '/jobs/period_jobs/new',
    '/restaurants/:restaurantId/*',
    '/attendances',
  ];
  const showRestaurantSelector = some(RESTAURANT_SELECTOR_PATHS, (pattern) => matchPath(pattern, location.pathname));
  const showRestaurantSelectorGroup = includes(['/jobs'], trimEnd(location.pathname, '/'));

  const showRestaurantJobHiringSelector = includes(['/job_hirings'], trimEnd(location.pathname, '/'));

  const authPageTitle = determineAuthPageTitle(location.pathname);

  const [{ isInApp }] = React.useState(() => InAppSpy());

  const signOutHandler = () => {
    navigate('/flow_sign_out', { replace: false });
  };

  const goBackHandler = () => {
    goBack();
  };

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const toggleDrawerHandler = () => {
    setOpenDrawer((prevOpenDrawer) => !prevOpenDrawer);
  };

  return (
    <>
      <AppBar position="fixed" sx={{ background: 'transparent', boxShadow: 'none' }}>
        {!hideNavBar ? (
          <AppBar position="static" data-testid="nav-bar">
            <Toolbar>
              {
                // eslint-disable-next-line no-nested-ternary
                showDrawerMenu ? (
                  <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    data-testid="nav-bar-menu-button"
                    onClick={toggleDrawerHandler}
                  >
                    <MenuIcon />
                  </IconButton>
                ) : showGoBackMenu ? (
                  <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    data-testid="nav-bar-back-button"
                    onClick={goBackHandler}
                  >
                    <ArrowBack />
                  </IconButton>
                ) : (
                  <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    data-testid="nav-bar-back-button"
                    onClick={signOutHandler}
                  >
                    <ArrowBack />
                  </IconButton>
                )
              }

              {showRestaurantSelector ? (
                <RestaurantSelector sx={{ width: '40%', maxWidth: 330 }} group={showRestaurantSelectorGroup} />
              ) : null}

              {showRestaurantJobHiringSelector ? (
                <RestaurantJobHiringSelector sx={{ width: '40%', maxWidth: 330 }} />
              ) : null}

              <div style={{ flexGrow: 1 }} />

              {authPageTitle ? (
                <>{authPageTitle}</>
              ) : (
                <>
                  <UserInfoMenuButton sx={{ maxWidth: '35%' }} />
                </>
              )}
            </Toolbar>
          </AppBar>
        ) : null}

        {isInApp ? (
          <AppBar
            position="static"
            sx={{ background: theme.palette.background.default, boxShadow: 'none', zIndex: -1 }}
          >
            <Toolbar variant="dense">
              <div style={{ flexGrow: 1 }} />

              <OpenBrowserButton variant="outlined" size="small">
                {navBarI18n('open_browser')}
              </OpenBrowserButton>
            </Toolbar>
          </AppBar>
        ) : null}
      </AppBar>

      {!hideNavBar ? <Toolbar /* Use as spacer to push content out of Main AppBar */ /> : null}
      {isInApp ? <Toolbar variant="dense" /* Use as spacer to push content out of Open Browser AppBar */ /> : null}

      <AppDrawer open={openDrawer} onClose={toggleDrawerHandler} />
    </>
  );
};

export default NavBar;
